/**
 * Alle Stellschrauben an einem Ort.
 *
 * Die Zeitschwellen sind begründete Startwerte, keine gemessenen. Nach zwei
 * Wochen echter Nutzung werden sie anhand der Antwort-Historie nachjustiert.
 * Deshalb stehen sie hier und nicht im Code verstreut.
 */

export const APP_NAME = 'Zahlenflug'

/** Aufgaben pro Tagesrunde. Hartes Ende, kein endloser Strom. */
export const AUFGABEN_PRO_RUNDE = 20

/**
 * Hoechstzahl verschiedener Karten pro Runde.
 *
 * Bei 20 verschiedenen Karten gaebe es innerhalb der Runde keine einzige
 * Wiederholung. Die Wiederholung in der Sitzung ist aber genau der
 * Mechanismus, der kurzfristig festigt.
 */
export const KARTEN_PRO_RUNDE = 12

export type Format = 'ballons' | 'zahlenfeld' | 'ritt'
export type Zone = 'gewusst' | 'gerechnet' | 'hakt'

/**
 * Zeitschwellen je Format, in Millisekunden.
 *
 * Gemessen wird die Zeit bis zur ERSTEN Eingabe, nicht bis zum Absenden:
 * 4 x 2 = 8 ist eine Taste, 6 x 7 = 42 sind zwei. Bis zum Absenden gemessen
 * waere jede zweistellige Antwort kuenstlich langsamer.
 *
 * Die Ballon-Schwellen liegen hoeher, weil das Lesen von vier Optionen eine
 * konstante Grundlast erzeugt, die nichts mit dem Abruf zu tun hat.
 */
export const ZEITSCHWELLEN: Record<Format, { gewusst: number; gerechnet: number }> = {
  zahlenfeld: { gewusst: 3000, gerechnet: 8000 },
  ballons: { gewusst: 4000, gerechnet: 9000 },
  // Der Ausritt zeigt ebenfalls vier Antwortflächen, also dieselbe Leselast.
  ritt: { gewusst: 4000, gerechnet: 9000 },
}

/** Leitner-Fächer: Index = Fach, Wert = Tage bis zur nächsten Fälligkeit. */
export const FACH_INTERVALLE = [0, 1, 2, 4, 7, 14, 30] as const

export const MAX_FACH = FACH_INTERVALLE.length - 1

/**
 * Rueckstufung bei "hakt".
 *
 * Nicht auf 0 zuruecksetzen: Ein einzelner Ausrutscher bei einer Karte in
 * Fach 5 soll sie nicht auf Anfang werfen. Fach 3 bedeutet 4 Tage, das ist
 * haeufig genug zur Korrektur und nicht entmutigend.
 */
export const RUECKSTUFUNG_BEI_HAKT = 2

/**
 * Erstkontakt-Sprung: Wird eine Karte beim allerersten Mal sofort gewusst,
 * springt sie direkt auf dieses Fach statt auf 1. Beschleunigt die Einstufung
 * in den ersten Tagen erheblich, weil Lina den Grossteil schon kann, und
 * erspart eine gesonderte Einstufungsrunde.
 */
export const ERSTKONTAKT_FACH = 2

/** Nach wie vielen anderen Aufgaben eine hakende Karte erneut gestellt wird. */
export const WIEDERVORLAGE_ABSTAND_MIN = 2
export const WIEDERVORLAGE_ABSTAND_MAX = 3

/** Wie oft eine einzelne Karte pro Runde hoechstens wiedervorgelegt wird. */
export const WIEDERVORLAGEN_PRO_KARTE = 2

/** Anteil der Aufgaben im Ballon-Format (Rest: Zahlenfeld). */
export const BALLON_ANTEIL = 0.7

/** Ab diesem Fach werden auch Divisionsaufgaben gestellt. */
export const DIVISION_AB_FACH = 2

/** Gewichtung Multiplikation zu Division, sobald Division erlaubt ist. */
export const MULTIPLIKATION_GEWICHT = 2
export const DIVISION_GEWICHT = 1

/** Richtige in Folge, bis die Kette zuendet. */
export const KETTE_AB = 5

/**
 * Unter dieser Zeit gilt eine Antwort als blitzschnell.
 *
 * Deutlich unter der Schwelle für "gewusst": Hier geht es nicht mehr um die
 * Einstufung der Karte, sondern um ein sichtbares Extra für genau das
 * Verhalten, das die App trainieren soll. Eine Belohnung, die an echtes
 * Koennen geknuepft ist und die das Kind jederzeit selbst wiederholen kann.
 */
export const BLITZ_MS = 1500

/** Freie Tage pro Woche, die die Serie nicht reissen lassen. */
export const JOKER_PRO_WOCHE = 1

/** Groesster Faktor im kleinen Einmaleins. */
export const MAX_FAKTOR = 10

/**
 * Kopplung: Adresse des WebSocket-Relays.
 * Im Betrieb liefert derselbe Host die App aus, deshalb relativ.
 */
export const RELAY_PFAD = '/relay'

/** Gueltigkeit eines Kopplungs-Codes ohne Verbindung, in Minuten. */
export const CODE_GUELTIG_MINUTEN = 30

/**
 * Ab dieser Antwortzeit gilt eine Aufgabe als unterbrochen, nicht als langsam.
 *
 * Der Unterschied ist wichtig: Wird Lina beim Ueben gerufen, legt das Tablet
 * weg und tippt zehn Minuten spaeter die richtige Antwort, dann hat sie die
 * Aufgabe gewusst. Ohne diesen Deckel wuerde die Karte stattdessen zwei
 * Fächer zurückfallen, und der Scheduler hätte einen Wert gelernt, der nie
 * gemessen wurde. Solche Antworten werden gar nicht gewertet, die Aufgabe
 * wird einfach erneut gestellt.
 */
export const PAUSE_SCHWELLE_MS = 45_000

/** Ballonfarben, die mit der Zeit dazukommen. Siehe src/spiel/ballonsets.ts. */
export const BALLONSET_STANDARD = 'abendhimmel'

/**
 * Sternental: ab wann ein Herdenpferd eine Stufe weiter ist.
 *
 * Gezählt werden die zehn Karten einer Reihe und ihr Leitner-Fach. Fach 2
 * erreicht eine Karte schon beim ersten schnellen Treffer, Fach 4 erst nach
 * mehreren gewussten Antworten an verschiedenen Tagen. Heimgekehrt heißt
 * also: Die Reihe sitzt wirklich, nicht nur heute.
 */
export const HERDE_SCHWELLEN = {
  gesichtet: { anzahl: 5, fach: 2 },
  vertrauen: { anzahl: 8, fach: 3 },
  heimgekehrt: { anzahl: 10, fach: 4 },
} as const

/**
 * Ankunftszeit eines Hindernisses im Ausritt, in Millisekunden.
 *
 * Startet bei Linas letzter Zeit für die Karte plus großzügigem Puffer,
 * höchstens 12 s, und sinkt mit schnellen Antworten bis 8 s.
 *
 * Ursprünglich 4 bis 6 s, damit „pünktlich am Hindernis“ genau „gewusst“
 * bedeutet. Im ersten Test mit Lina (10.10.2026) war das zu knapp: Das Pferd
 * blieb ständig stehen, und das fühlte sich nach Druck an statt nach Reiten.
 * Das Tempo-Ziel misst weiter der Scheduler über die Zonen, das Hindernis
 * muss es nicht zusätzlich erzwingen.
 */
export const RITT_ANKUNFT_MIN_MS = 8000
export const RITT_ANKUNFT_MAX_MS = 12000
export const RITT_ANKUNFT_PUFFER = 1.5

/**
 * Flow im Ausritt: richtige und schnelle Antworten (Zone gewusst) in Folge.
 * Ab hier glitzern Mähne und Schweif.
 */
export const FLOW_GLITZER_AB = 3
/** Ab hier wird jeder Sprung ein Sternensprung. */
export const FLOW_STERNENSPRUNG_AB = 5
